// Our Advantage section layout data — follows the same pattern as heroLayouts.constants.js.
// Each layout is pure data ({ label, defaultProps }), no component attached.
// One shared StructuralOurAdvantagePreview renders all 3 layouts from the
// `displayStyle` config field — no per-layout component branch.

export const OUR_ADVANTAGE_DISPLAY_STYLE_OPTIONS = [
    { value: "stat-grid",  label: "Stat Grid" },
    { value: "large-stat", label: "Large Stat" },
    { value: "icon-grid",  label: "Icon Grid" },
];

// Card field definitions — previously only existed as CardsEditor.js's
// hardcoded fallback default (OUR_ADVANTAGE_CARD_FIELDS/OUR_ADVANTAGE_NEW_ITEM),
// the only cards-field consumer that didn't declare its own explicitly (see
// faqLayouts.constants.js's FAQ_ITEM_FIELDS/FAQ_ITEM_NEW_DEFAULT for the
// established convention this now matches). Values copied verbatim from
// CardsEditor.js — same field order, labels, and newItemDefault shape — so
// existing published rows continue to read identically. plan-v3.md Step 3.
// `big`/`unit` deliberately excluded from wrapControl — short numeric/unit
// strings (e.g. "20", "AED") have no meaningful wrap behavior, see
// plan-v3.md Step 4 scope note.
export const OUR_ADVANTAGE_CARD_FIELDS = [
    { field: "icon",        label: "Icon", type: "icon" },
    { field: "big",         label: "Stat number", translatable: true },
    { field: "unit",        label: "Unit",        translatable: true },
    { field: "title",       label: "Title",       translatable: true, wrapControl: true },
    { field: "description", label: "Description", translatable: true, wrapControl: true },
];
export const OUR_ADVANTAGE_NEW_ITEM = { icon: "StarOutlined", big: "", unit: "", title: "New Advantage", description: "" };

// Layout 1 — MEELZ-inspired: equal-column grid of white cards,
// stat (big + unit) above a divider, title + description below.
const LAYOUT_1_DEFAULT_PROPS = {
    displayStyle:    "stat-grid",
    sectionTitle:    "Our Advantages",
    backgroundColor: "#F9F5EE",
    textColor:       "#1a1a1a",
    primaryColor:    "#C9A96E",
    iconColor:       "#C9A96E",
    cards: [
        { icon: "TruckOutlined",     big: "0",         unit: "AED", title: "Free Delivery",     description: "Flexibly schedule delivery in 2-hour intervals" },
        { icon: "DashboardOutlined", big: "Nutrients", unit: "",    title: "We Count Nutrients", description: "We calculate all nutrients so you never have to" },
        { icon: "ClockCircleOutlined", big: "20",      unit: "h",   title: "Time Saving",        description: "Direct your time to rest, not cooking" },
        { icon: "FireOutlined",      big: "40",        unit: "°",   title: "Just Heat It",       description: "Your dish only needs to be heated. Ready!" },
    ],
};

// Layout 2 — CK-inspired: no card containers, very large light-weight numbers,
// primaryColor accent on the unit, title + description below.
const LAYOUT_2_DEFAULT_PROPS = {
    displayStyle:    "large-stat",
    sectionTitle:    "Advantages",
    backgroundColor: "#E8F0F9",
    textColor:       "#1a1a1a",
    primaryColor:    "#22C55E",
    iconColor:       "#22C55E",
    cards: [
        { icon: "", big: "0",    unit: "₾",    title: "Free Delivery",     description: "Flexibly schedule delivery in 2-hour intervals" },
        { icon: "", big: "1600", unit: "kcal", title: "We Count Calories",  description: "You no longer need to worry about counting calories" },
        { icon: "", big: "20",   unit: "h",    title: "Time Saving",        description: "Direct your time to personal matters or rest" },
        { icon: "", big: "40",   unit: "°",    title: "Just Heat It",       description: "Your dish only needs to be heated. Ready!" },
        { icon: "", big: "+",    unit: "",     title: "Healthy Food",       description: "We cook only with fresh ingredients" },
    ],
};

// Layout 3 — Icon-focused: large emoji at top, primaryColor top-border accent,
// title + description. No stat numbers needed.
const LAYOUT_3_DEFAULT_PROPS = {
    displayStyle:    "icon-grid",
    sectionTitle:    "Why Choose Us",
    backgroundColor: "#FFFFFF",
    textColor:       "#1a1a1a",
    primaryColor:    "#573121",
    iconColor:       "#573121",
    cards: [
        { icon: "RocketOutlined",            big: "", unit: "", title: "Fast Delivery",        description: "Get your meals delivered fresh to your door" },
        { icon: "GiChefToque",                big: "", unit: "", title: "Expert Chefs",         description: "Every meal crafted by professional nutritionist chefs" },
        { icon: "DashboardOutlined",          big: "", unit: "", title: "Nutritional Tracking",  description: "Full macro breakdown with every meal" },
        { icon: "SafetyCertificateOutlined",  big: "", unit: "", title: "Eco Packaging",         description: "All packaging is sustainable and recyclable" },
    ],
};

// CK: real, brand-owned entry (plan.md Decision 2 / Step 4.6) — confirmed
// identical to CK's actual live content: section title "Advantages" matches
// ck-client-ck's own t("advantages") en translation exactly, background
// #E8F0F9 matches chef-kitchen.tsx's own section wrapper background, and
// the 5-card content/order is byte-for-byte the same as advantages-section.tsx's
// real hardcoded cards. layout_2 ("CK-inspired") already happened to match
// this real content — this new key makes it a genuine brand-owned layout
// the same way hero/navbar/footer already have `ck` keys, not just a
// coincidentally-similar generic slot every brand can pick.
const OUR_ADVANTAGE_CK_DEFAULT_PROPS = {
    displayStyle:    "large-stat",
    sectionTitle:    "Advantages",
    backgroundColor: "#E8F0F9",
    textColor:       "#1a1a1a",
    primaryColor:    "#22C55E",
    iconColor:       "#22C55E",
    cards: [
        { icon: "", big: "0",    unit: "₾",    title: "Free Delivery",     description: "Flexibly schedule delivery in 2-hour intervals" },
        { icon: "", big: "1600", unit: "kcal", title: "We Count Calories",  description: "You no longer need to worry about counting calories" },
        { icon: "", big: "20",   unit: "h",    title: "Time Saving",        description: "Direct your time to personal matters or rest" },
        { icon: "", big: "40",   unit: "°",    title: "Just Heat It",       description: "Your dish only needs to be heated. Ready!" },
        { icon: "", big: "+",    unit: "",     title: "Healthy Food",       description: "We cook only with fresh ingredients" },
    ],
};

// Layout IDs are generic strings, not brand names — a future layout can be
// named by design intent (e.g. "layout_4") with no brand tie-in.
export const OUR_ADVANTAGE_LAYOUTS = {
    layout_1: { label: "Layout 1 — Stat Grid",  defaultProps: LAYOUT_1_DEFAULT_PROPS },
    layout_2: { label: "Layout 2 — Large Stat", defaultProps: LAYOUT_2_DEFAULT_PROPS },
    layout_3: { label: "Layout 3 — Icon Grid",  defaultProps: LAYOUT_3_DEFAULT_PROPS },
    ck:       { label: "Our Advantage (CK)",    defaultProps: OUR_ADVANTAGE_CK_DEFAULT_PROPS },
};

// One shared schema for all 3 layouts — `displayStyle` is the structural
// differentiator that StructuralOurAdvantagePreview branches on, same way
// hero uses `arrangement`/`alignment`.
export const OUR_ADVANTAGE_SCHEMA = [
    { key: "displayStyle",    label: "Display style",    type: "select", options: OUR_ADVANTAGE_DISPLAY_STYLE_OPTIONS },
    { key: "sectionTitle",    label: "Section title",    type: "text", translatable: true, wrapControl: true },
    { key: "backgroundColor", label: "Background color", type: "color" },
    { key: "textColor",       label: "Text color",       type: "color" },
    { key: "primaryColor",    label: "Primary color",    type: "color" },
    { key: "iconColor",       label: "Icon color",       type: "color" },
    {
        key:            "cards",
        label:          "Cards",
        type:           "cards",
        fields:         OUR_ADVANTAGE_CARD_FIELDS,
        newItemDefault: OUR_ADVANTAGE_NEW_ITEM,
    },
];
